import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../../design-system';
import '../../styles/TallyPanel.css';

// Which columns of a wide table are on screen.
//
// ⚠️ NOT THE `Menu` PRIMITIVE, and the reason is behaviour rather than looks.
// `Menu` closes on select, which is right for a list of actions and wrong for a
// list of checkboxes: hiding four columns would mean opening the panel four
// times. This stays open until it is dismissed.
//
// ⚠️ THE CHECKBOX SAYS "SHOW", NOT "HIDE". A ticked box meaning "hidden" is
// readable but inverts what every settings list in the product does, and the
// column is either on the screen or it is not — that is the fact the reader is
// looking at.

function ColumnPicker({ columns, hidden, onToggle, onShowAll }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const panelId = useId();

  // Dismiss on an outside click or Escape. Both, because this panel holds no
  // focusable dismissal of its own and a reader who has finished with it will
  // reach for whichever is closer to hand.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // A column marked `always` is load-bearing and is not offered.
  const optional = columns.filter((c) => !c.always);
  const hiddenCount = optional.filter((c) => hidden.has(c.key)).length;

  return (
    <div className="tally-colpick" ref={wrapRef}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
      >
        {/* ⚠️ THE COUNT IS ON THE TRIGGER. A reader who hid six columns last
            month and has forgotten will otherwise conclude the data is missing
            rather than that they switched it off — this is the whole failure
            mode of remembering a preference between visits. */}
        {hiddenCount ? `Columns (${hiddenCount} hidden)` : 'Columns'}
      </Button>

      {open && (
        <div className="tally-colpick-panel" id={panelId}>
          <div className="tally-colpick-head">
            <span className="tally-kpi-label">Show these columns</span>
            {hiddenCount > 0 && (
              <button type="button" className="tally-linkish tally-linkish-inline" onClick={onShowAll}>
                Show all
              </button>
            )}
          </div>

          <div className="tally-colpick-list">
            {optional.map((c) => (
              <label key={c.key} className="tally-colpick-item">
                <input
                  type="checkbox"
                  checked={!hidden.has(c.key)}
                  onChange={() => onToggle(c.key)}
                />
                <span>
                  {c.label || c.header}
                  {/* Several columns are only meaningful on some rows — GST on a
                      sale, stock on a trading voucher. Saying so beats a reader
                      switching one on, seeing a column of dashes and concluding
                      the sync is broken. */}
                  {c.note && <span className="tally-colpick-note">{c.note}</span>}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ColumnPicker;
